import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/landingpage/Header';
import Footer from '../../components/landingpage/Footer';

const BLOGS_SEARCH_URL = '/api/globals/collection/blogs/search';
const BLOG_TYPES_URL   = '/api/globals/collection/blog-types?limit=50&sortBy=sortOrder&sortOrder=asc';

interface BlogPostItem {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  categoryId: string;
  image: string;
  slug: string;
  viewCount: number;
  isFeatured: boolean;
  tags: string[];
}

interface BlogType {
  id: string;
  name: string;
  slug: string;
  color: string;
}

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&q=80';

function mapItem(item: any): BlogPostItem {
  const d = item.data ?? {};
  // navigate resolves 'Blog Type' ID → object: d['Blog Type'] = { id, data: { name, color, ... } }
  const typeData = (d['Blog Type'] && typeof d['Blog Type'] === 'object') ? d['Blog Type'].data ?? {} : {};
  const rawDate = d['Publish Date'];
  const publishDate = rawDate ? new Date(rawDate).toLocaleDateString('vi-VN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  return {
    id: item.id ?? item._id,
    title: d.Title ?? '',
    excerpt: d.Excerpt ?? '',
    author: d.Author ?? 'Admin',
    date: publishDate,
    category: typeData.name ?? '',
    categoryId: typeof d['Blog Type'] === 'object' ? d['Blog Type']?.id ?? '' : d['Blog Type'] ?? '',
    image: d.Thumbnail || PLACEHOLDER_IMAGE,
    slug: d.Slug ?? '',
    viewCount: 0,
    isFeatured: d.Featured ?? false,
    tags: d.Tags ?? [],
  };
}

// ── Sub-components ─────────────────────────────────────────────────────────────

const BlogCard: React.FC<{ post: BlogPostItem }> = ({ post }) => (
  <article className="flex flex-col group">
    {/* Image */}
    <Link to={`/blogs/${post.slug}`} className="block relative overflow-hidden rounded-2xl aspect-[4/3] mb-4">
      <img
        src={post.image}
        alt={post.title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        onError={(e) => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&q=80'; }}
      />
      {/* Author + category overlay */}
      <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-4 bg-gradient-to-t from-[#017e6e]/80 to-transparent">
        <div>
          <p className="text-white text-sm font-semibold leading-tight">{post.author}</p>
          <p className="text-white/80 text-xs mt-0.5">{post.date}</p>
        </div>
        {post.category && (
          <span className="text-white text-xs font-semibold bg-[#017e6e] px-2.5 py-1 rounded-full">
            {post.category}
          </span>
        )}
      </div>
    </Link>

    {/* Content */}
    <div className="flex flex-col flex-1">
      <Link
        to={`/blogs/${post.slug}`}
        className="text-xl font-semibold text-gray-800 leading-snug hover:text-[#017e6e] transition-colors"
      >
        {post.title}
      </Link>
      <p className="mt-2 text-gray-500 text-sm leading-relaxed flex-1">{post.excerpt}</p>
      <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
        <span>{post.viewCount} lượt xem</span>
        {post.tags.length > 0 && (
          <>
            <span>·</span>
            <span>#{post.tags[0]}</span>
          </>
        )}
      </div>
      <Link
        to={`/blogs/${post.slug}`}
        className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#017e6e] hover:text-[#015f54] transition-colors"
      >
        Đọc bài
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 17 17 7M7 7h10v10" />
        </svg>
      </Link>
    </div>
  </article>
);

// ── Main Page ──────────────────────────────────────────────────────────────────

const BlogListPage: React.FC = () => {
  const [posts, setPosts]             = useState<BlogPostItem[]>([]);
  const [blogTypes, setBlogTypes]     = useState<BlogType[]>([]);
  const [activeTypeId, setActiveTypeId] = useState<string | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(0);
  const [totalDocs, setTotalDocs]     = useState(0);

  // Load blog-types for category tabs
  useEffect(() => {
    fetch(BLOG_TYPES_URL)
      .then((r) => r.json())
      .then((json) => {
        const items: BlogType[] = (json?.data?.items ?? []).map((item: any) => ({
          id: item.id ?? item._id,
          name: item.data?.name ?? '',
          slug: item.data?.slug ?? '',
          color: item.data?.color ?? '#017e6e',
        }));
        setBlogTypes(items);
      })
      .catch(() => {/* silently ignore — tabs just won't show */});
  }, []);

  const fetchPosts = useCallback(async (p: number, typeId: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const groups: any[] = [{ field: 'Public', op: { $eq: true } }];
      if (typeId) groups.push({ field: 'Blog Type', op: { $eq: typeId } });

      const body = {
        page: p,
        limit: 9,
        sortBy: 'Publish Date',
        sortOrder: 'desc',
        navigate: ['Blog Type'],
        filterGroup: { type: 'and', groups },
      };

      const res  = await fetch(BLOGS_SEARCH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      setPosts((json?.data?.items ?? []).map(mapItem));
      const pg = json?.data?.pagination ?? {};
      setTotalPages(pg.pages ?? 0);
      setTotalDocs(pg.total ?? 0);
    } catch (err) {
      setError('Không thể tải bài viết. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts(page, activeTypeId);
  }, [page, activeTypeId, fetchPosts]);

  const handleTypeChange = (typeId: string | null) => {
    setActiveTypeId(typeId);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1">
        {/* ── Hero ────────────────────────────────────────────────────── */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 className="text-5xl font-bold text-[#2e2e2e] tracking-tight">Blog</h1>
              <p className="mt-3 text-gray-500 text-lg max-w-sm">
                Bài viết mới nhất về IELTS, kinh nghiệm học và các mẹo thi đỉnh cao.
              </p>
            </div>
            {totalDocs > 0 && (
              <p className="text-sm text-gray-400">{totalDocs} bài viết</p>
            )}
          </div>
        </section>

        {/* ── Category tabs ───────────────────────────────────────────── */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto pb-px border-b border-gray-200 scrollbar-none">
            <button
              onClick={() => handleTypeChange(null)}
              className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                activeTypeId === null
                  ? 'border-[#017e6e] text-[#017e6e]'
                  : 'border-transparent text-gray-500 hover:text-[#017e6e]'
              }`}
            >
              Tất cả
            </button>
            {blogTypes.map((bt) => (
              <button
                key={bt.id}
                onClick={() => handleTypeChange(bt.id)}
                className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                  activeTypeId === bt.id
                    ? 'border-[#017e6e] text-[#017e6e]'
                    : 'border-transparent text-gray-500 hover:text-[#017e6e]'
                }`}
              >
                {bt.name}
              </button>
            ))}
          </div>
        </section>

        {/* ── Blog grid ───────────────────────────────────────────────── */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-3 animate-pulse">
                  <div className="rounded-2xl aspect-[4/3] bg-gray-100" />
                  <div className="h-4 bg-gray-100 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-full" />
                  <div className="h-3 bg-gray-100 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-500 mb-4">{error}</p>
              <button onClick={() => fetchPosts(page, activeTypeId)} className="px-4 py-2 bg-[#017e6e] text-white rounded-lg text-sm">Thử lại</button>
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center text-gray-400 py-20">Chưa có bài viết trong danh mục này.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {posts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-12">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:border-[#017e6e] hover:text-[#017e6e] transition-colors"
              >
                ← Trước
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                    n === page
                      ? 'bg-[#017e6e] text-white border-[#017e6e]'
                      : 'border-gray-200 hover:border-[#017e6e] hover:text-[#017e6e]'
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:border-[#017e6e] hover:text-[#017e6e] transition-colors"
              >
                Sau →
              </button>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogListPage;

